import OverwatchModal from "@/components/OverwatchModal";
import UploadPreviewItem from "@/components/UploadPreviewItem";
import { MarkAsCollectedDocument } from "@/graphql/generated/graphql";
import useFileUploader from "@/hooks/useFileUploader";
import { MAX_ATTACHMENTS_ALLOWED } from "@/utils/constants";
import { arrayLength } from "@/utils/helperFunctions";
import { displayNotification } from "@/utils/notifications";
import { useMutation } from "@apollo/client/react";
import { Attachment } from "@mui/icons-material";
import { Box, Button, Grid, styled, TextField } from "@mui/material";
import { isEmpty, map } from "lodash";
import { map as fpMap } from "lodash/fp";
import { useState } from "react";

interface CollectedModalProps {
  handleClose: () => void;
  stateId: string;
}

interface CollectedData {
  id: string;
  delivererName: string;
  receiverName: string;
  notes: string;
  signedIds: string[];
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const mapSignedBlobId = fpMap("signedBlobId");

const CollectedModal: React.FC<CollectedModalProps> = ({ handleClose, stateId }) => {
  const [collectedData, setCollectedData] = useState<CollectedData>({
    id: stateId,
    delivererName: "",
    receiverName: "",
    notes: "",
    signedIds: []
  });
  const { isUploading, files, handleUploadImage, handleRemoveImage, shouldDisableUploadButton } = useFileUploader();

  const [markAsCollected] = useMutation(MarkAsCollectedDocument, {
    onCompleted: () => {
      displayNotification({ text: "Delivery And Collection Item marked as collected successfully", type: "success" });
    },
    onError: (error) => {
      displayNotification({ text: error.message, type: "error" });
    },
  });

  const handleSave = () => {
    markAsCollected({
      variables: {
        id: collectedData.id,
        delivererName: collectedData.delivererName,
        receiverName: collectedData.receiverName,
        notes: collectedData.notes,
        signedIds: mapSignedBlobId(files),
      },
      update(cache) {
        cache.evict({ id: cache.identify({ __typename: "DeliveryAndCollectionItemState", id: collectedData.id }) })
        cache.gc();
      },
    });

    handleClose();
  };

  return (
    <OverwatchModal
      title="Delivery / Collection"
      caption="Including service attendance"
      primaryButtonText={'Save'}
      primaryButtonAction={handleSave}
      secondaryButtonText='Cancel'
      secondaryButtonAction={handleClose}
      handleClose={handleClose}
      size="md"
    >
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={6}>
          <TextField
            label="Deliverer Name"
            value={collectedData.delivererName}
            onChange={(e) => setCollectedData((prev) => ({ ...prev, delivererName: e.target.value }))}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid size={6}>
          <TextField
            label="Receiver Name"
            value={collectedData.receiverName}
            onChange={(e) => setCollectedData((prev) => ({ ...prev, receiverName: e.target.value }))}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid size={12}>
          <TextField
            label="Notes"
            value={collectedData.notes}
            onChange={(e) => setCollectedData((prev) => ({ ...prev, notes: e.target.value }))}
            size="small"
            fullWidth
            multiline
            rows={8}
          />
        </Grid>
        <Grid container alignItems="center" size={6}>
          <Button
            component="label"
            variant="contained"
            tabIndex={-1}
            startIcon={<Attachment />}
            sx={{ marginTop: 1 }}
            disabled={shouldDisableUploadButton}
          >
            Attach images
            <VisuallyHiddenInput
              type="file"
              accept="image/png, image/jpeg"
              onChange={handleUploadImage}
              disabled={arrayLength(collectedData.signedIds) >= MAX_ATTACHMENTS_ALLOWED}
            />
          </Button>
        </Grid>
        <Box width="100%">
          {!isEmpty(files) &&
            map(files, (file, index) => (
              <UploadPreviewItem
                file={file}
                isUploading={isUploading}
                key={`${index}-${file.signedBlobId}`}
                onRemove={() => handleRemoveImage(file?.signedBlobId as string)}
              />
            ))
          }
        </Box>
      </Grid>
    </OverwatchModal>
  );
};

export default CollectedModal;
